import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AlertCircle, RotateCcw, TrendingUp, TrendingDown, Info } from 'lucide-react'
import type { Asset } from '@/types/api'

// =============================================================================
// Schema
// =============================================================================

const assetShockSchema = z.record(
  z.number().min(-100).max(1000)
)

type AssetShockFormData = z.infer<typeof assetShockSchema>

// =============================================================================
// Props
// =============================================================================

interface CustomShocksFormProps {
  assets: Asset[]
  assetShocks: Record<string, number>
  onAssetShockChange: (assetId: string, shock: number) => void
  onResetAll: () => void
}

// =============================================================================
// Helper Functions
// =============================================================================

function getAssetClassLabel(assetClass: string): string {
  return assetClass === 'CRYPTO' ? 'Crypto' : 'TradFi'
}

function getAssetClassBadgeColor(assetClass: string): string {
  return assetClass === 'CRYPTO'
    ? 'bg-orange-100 text-orange-800 border-orange-200'
    : 'bg-blue-100 text-blue-800 border-blue-200'
}

// =============================================================================
// Component
// =============================================================================

export function CustomShocksForm({
  assets,
  assetShocks,
  onAssetShockChange,
  onResetAll,
}: CustomShocksFormProps) {
  const [activeAssetClass, setActiveAssetClass] = useState<'all' | 'CRYPTO' | 'TRADFI'>('all')

  const { } = useForm<AssetShockFormData>({
    resolver: zodResolver(assetShockSchema),
    defaultValues: assetShocks,
  })

  // Group assets by class
  const cryptoAssets = assets.filter((a) => a.assetClass === 'CRYPTO')
  const tradfiAssets = assets.filter((a) => a.assetClass === 'TRADFI')

  // Filter assets based on selection
  const filteredAssets =
    activeAssetClass === 'all'
      ? assets
      : assets.filter((a) => a.assetClass === activeAssetClass)

  // Bulk action handlers
  const handleSetAllCrypto = (value: number) => {
    cryptoAssets.forEach((asset) => {
      onAssetShockChange(asset.id, value)
    })
  }

  const hasAnyShocks = Object.keys(assetShocks).length > 0

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header with bulk actions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                Asset Shock Configuration
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p className="text-sm">
                      Specify percentage moves for individual assets. Positive values = gain, negative = loss.
                      Range: -100% to +1000%.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSetAllCrypto(-50)}
                  disabled={cryptoAssets.length === 0}
                >
                  <TrendingDown className="h-4 w-4 mr-1" />
                  Set All Crypto -50%
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onResetAll}
                  disabled={!hasAnyShocks}
                >
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset All
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveAssetClass('all')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeAssetClass === 'all'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                All Assets
              </button>
              <button
                onClick={() => setActiveAssetClass('CRYPTO')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeAssetClass === 'CRYPTO'
                    ? 'bg-orange-500 text-white'
                    : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                }`}
              >
                Crypto ({cryptoAssets.length})
              </button>
              <button
                onClick={() => setActiveAssetClass('TRADFI')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeAssetClass === 'TRADFI'
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                }`}
              >
                TradFi ({tradfiAssets.length})
              </button>
            </div>

            {/* Shocks table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Asset</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead>Current Price</TableHead>
                    <TableHead className="w-48">Shock %</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No assets in this category
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAssets.map((asset) => (
                      <AssetShockRow
                        key={asset.id}
                        asset={asset}
                        shockValue={assetShocks[asset.id]}
                        onShockChange={(value) => onAssetShockChange(asset.id, value)}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Summary */}
            {hasAnyShocks && (
              <div className="mt-4 p-3 bg-muted rounded-md">
                <div className="flex items-center gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span>
                    <strong>{Object.keys(assetShocks).length}</strong> assets configured with custom shocks
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  )
}

// =============================================================================
// Asset Shock Row Component
// =============================================================================

interface AssetShockRowProps {
  asset: Asset
  shockValue?: number
  onShockChange: (value: number) => void
}

function AssetShockRow({ asset, shockValue = 0, onShockChange }: AssetShockRowProps) {
  const [localValue, setLocalValue] = useState<string>(shockValue ? shockValue.toString() : '0')

  const handleBlur = () => {
    const numValue = parseFloat(localValue) || 0
    // Clamp to valid range
    const clamped = Math.max(-100, Math.min(1000, numValue))
    setLocalValue(clamped.toString())
    onShockChange(clamped)
  }

  const isNegative = (parseFloat(localValue) || 0) < 0
  const isConfigured = shockValue !== 0

  return (
    <TableRow className={isConfigured ? 'bg-primary/5' : undefined}>
      <TableCell>
        <div className="font-medium">{asset.symbol}</div>
        <div className="text-sm text-muted-foreground">{asset.name}</div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className={getAssetClassBadgeColor(asset.assetClass)}>
          {getAssetClassLabel(asset.assetClass)}
        </Badge>
      </TableCell>
      <TableCell>${parseFloat(asset.currentPrice).toLocaleString()}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const newValue = (parseFloat(localValue) || 0) - 5
              setLocalValue(newValue.toString())
              onShockChange(newValue)
            }}
            className="w-8 h-8 rounded-md bg-muted hover:bg-muted/80 flex items-center justify-center"
          >
            -
          </button>
          <div className="relative flex-1">
            <Input
              type="number"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={handleBlur}
              className={`text-center ${isNegative ? 'text-red-600' : 'text-green-600'}`}
              min={-100}
              max={1000}
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
              %
            </span>
          </div>
          <button
            onClick={() => {
              const newValue = (parseFloat(localValue) || 0) + 5
              setLocalValue(newValue.toString())
              onShockChange(newValue)
            }}
            className="w-8 h-8 rounded-md bg-muted hover:bg-muted/80 flex items-center justify-center"
          >
            +
          </button>
        </div>
      </TableCell>
      <TableCell className="text-right">
        {isConfigured && (
          <Badge variant={isNegative ? 'destructive' : 'default'}>
            {isNegative ? (
              <TrendingDown className="h-3 w-3 mr-1" />
            ) : (
              <TrendingUp className="h-3 w-3 mr-1" />
            )}
            {localValue}%
          </Badge>
        )}
      </TableCell>
    </TableRow>
  )
}
